<template>
  <!-- Card -->
  <div ref="container" v-editable="item" class="cm-media-card" :class="`is-${mediaView}`">
    <!-- Card Media -->
    <CMMediaCardBase
      :item="item"
      :mediaView="mediaView"
      :youtubePrivacyWarning="youtubePrivacyWarning"
      class="card-media"
      :class="`is-${mediaView}`"
      :breakpointsWidthMap="breakpointsWidthMap" />

    <!-- Card text -->
    <div ref="content" class="content">
      <div class="text-container">
        <h3 v-if="item.headline" class="heading-sub-medium">
          {{ item.headline }}
        </h3>
        <p v-if="item.text" class="text-medium-regular" v-html="item.text"></p>
      </div>

      <!-- Card CTAs -->
      <UtilLinkItemIcon v-if="item.actions?.[0]" :blok="item.actions?.[0]" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useScroll, useTransition } from '@seb-platform/shared/composables';
import UtilLinkItemIcon from '~/components/storyblok/utils/UtilLinkItemIcon/UtilLinkItemIcon.vue';
import { useGsap } from '~/composables/useGsap';
import type { CmMediaItemStoryblok, RichtextStoryblok } from '~/types/storyblok-generated';
import CMMediaCardBase from './CMMediaCardBase.vue';

withDefaults(
  defineProps<{
    item: CmMediaItemStoryblok;
    mediaView: 'normal' | 'wide' | 'align-right' | 'align-left' | 'horizontal';
    youtubePrivacyWarning: RichtextStoryblok;
    breakpointsWidthMap?: Record<string, number>;
  }>(),
  { mediaView: 'normal' }
);

const { state: scrollState } = useScroll();
const { easeOut } = useGsap();
const { fadeSlide } = useTransition();
const fadeInDone = ref(false);

const $container = useTemplateRef('container');
const $content = useTemplateRef('content');

function fadeIn() {
  if (fadeInDone.value) {
    return;
  }
  fadeInDone.value = true;

  const container = $container.value;

  const delay = ((container?.offsetLeft || 0) / window.innerWidth) * 0.8;
  const mediaWrapperEl = container!.querySelector('.card-media') as HTMLElement;
  const mediaEl = container!.querySelector('.media-wrapper') as HTMLElement;
  gsap.set(mediaWrapperEl, { autoAlpha: 0, scale: 0.9 });
  gsap.set(mediaEl, { scale: 1.25 });
  const shared = { ease: easeOut, delay, scale: 1, duration: 1 };
  gsap.to(mediaWrapperEl, { ...shared, autoAlpha: 1 });
  gsap.to(mediaEl, { ...shared });

  fadeSlide($content.value as HTMLElement, {
    baseDelay: delay + 0.5,
  });
}

function onScroll() {
  if (!$container.value) {
    return;
  }

  if (ScrollTrigger.isInViewport($container.value, 0.25)) {
    fadeIn();
  }
}

watch(
  () => [scrollState.top],
  () => onScroll()
);

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger);
  if (!$container.value) {
    return;
  }

  void nextTick(onScroll);
});
</script>

<style src="./CMMediaCard.scss" lang="scss" scoped />
